/**
 * SourceCard — renders one citation from /answer with the Task 044 field
 * guarantee. The visual treatment mirrors the design's "passage" card:
 * gold reference bar at top, body with collection + path + timestamp +
 * YouTube CTA. NEVER post-processes the source text — sacred-name
 * discipline applies (see docs/public-chat-architecture.md §6).
 */
import type { AnswerCitation } from "../api/answer-client";
import { citationReaderUrl, connectionsPath, softNavigate } from "../corpus/use-corpus-route";
import type { Strings } from "../strings";
import { isEmbedded } from "../embed/embed-mode";

export interface SourceCardProps {
  citation: AnswerCitation;
  strings: Strings;
}

function pad(value: number): string {
  return value < 10 ? `0${value}` : `${value}`;
}

function formatTimestamp(totalSeconds: number): string {
  const seconds = Math.max(0, Math.floor(totalSeconds));
  const hh = Math.floor(seconds / 3600);
  const mm = Math.floor((seconds % 3600) / 60);
  const ss = seconds % 60;
  return `${pad(hh)}:${pad(mm)}:${pad(ss)}`;
}

/**
 * The cleaned transcripts mark each passage with a `### [SECTION]` heading, which
 * the ingest folds into BOTH the citation title (appended as " - [SECTION]") and
 * the start of the excerpt. When one transcript segment is split into several
 * passages, every sibling card then shows the SAME video title + timestamp and
 * the distinguishing label is either truncated off the title or buried at the
 * front of the excerpt — so the cards read as duplicates.
 *
 * This lifts that `[SECTION]` label out into its own field: it becomes the
 * prominent, per-passage header, and is stripped from the title (de-duped) and
 * from the front of the excerpt (no redundant repeat). Pure string work; the
 * excerpt body text is otherwise untouched (sacred-name discipline preserved).
 */
function splitSection(citation: AnswerCitation): {
  section: string | null;
  title: string;
  excerpt: string | undefined;
} {
  let section: string | null = null;
  let excerpt = citation.excerpt;

  if (typeof excerpt === "string") {
    const lead = excerpt.match(/^\s*\[([^\]]+)\]\s*/);
    if (lead && typeof lead[1] === "string") {
      section = lead[1].trim();
      excerpt = excerpt.slice(lead[0]?.length ?? 0).trim();
    }
  }

  // Fallback: pull the label from a trailing " - [SECTION]" on the title.
  let title = citation.title;
  const trailing = title.match(/\s*[-–—]\s*\[([^\]]+)\]\s*$/);
  if (trailing && typeof trailing[1] === "string") {
    if (section === null) section = trailing[1].trim();
    title = title.slice(0, trailing.index).trim();
  }

  return { section, title, excerpt: excerpt && excerpt.length > 0 ? excerpt : undefined };
}

function buildYouTubeHref(citation: AnswerCitation): string | null {
  if (typeof citation.url !== "string" || citation.url.length === 0) return null;
  if (typeof citation.timestamp_start !== "number") return citation.url;
  const offset = Math.max(0, Math.floor(citation.timestamp_start));
  // Preserve any existing query string on the URL the wire delivered.
  const sep = citation.url.includes("?") ? "&" : "?";
  return `${citation.url}${sep}t=${offset}`;
}

export function SourceCard(props: SourceCardProps): JSX.Element {
  const { citation, strings } = props;
  const embedded = isEmbedded(); // memoized env fact, not state

  const href = buildYouTubeHref(citation);
  const readerUrl = citationReaderUrl(citation); // Task 190; null → link hidden
  const { section, title, excerpt } = splitSection(citation);

  const bookChapter =
    typeof citation.book === "string" && typeof citation.chapter === "number"
      ? strings.citationBookChapter(citation.book, citation.chapter)
      : citation.content_kind === "short"
        ? "Flechas"
        : null;

  const hasStart = typeof citation.timestamp_start === "number";
  const hasEnd = typeof citation.timestamp_end === "number";
  const timestampLabel = hasStart
    ? hasEnd
      ? `${formatTimestamp(citation.timestamp_start as number)}${strings.citationTimestampSeparator}${formatTimestamp(
          citation.timestamp_end as number,
        )}`
      : formatTimestamp(citation.timestamp_start as number)
    : strings.citationNoTimestamp;

  return (
    <article className="source">
      <header className="source__bar">
        <span className="source__ref" title={citation.title}>
          {title}
        </span>
        {bookChapter !== null ? (
          <span className="source__bc">{bookChapter}</span>
        ) : null}
      </header>
      <div className="source__body">
        {section !== null ? (
          // Per-passage section label (lifted out of the title/excerpt). This is
          // what distinguishes sibling passages from the same video segment, so
          // it leads the body, prominent and not truncated to one line.
          <p className="source__section">{section}</p>
        ) : null}
        <span className="source__timestamp">
          {citation.collection} · {timestampLabel}
        </span>
        {typeof excerpt === "string" && excerpt.length > 0 ? (
          // Optional verbatim passage preview (Task 078). Source text, not
          // metadata — rendered plain (no added quote marks), clamped in CSS.
          // Absent -> nothing rendered (no empty quote chrome).
          <p className="source__excerpt">{excerpt}</p>
        ) : null}
        <div className="source__actions">
          {readerUrl !== null ? (
            // Task 190: deep-link into the Corpus Reader at the exact section.
            // SPA navigation (softNavigate) keeps the chat in sessionStorage.
            <a
              className="source__reader"
              href={readerUrl}
              // Embedded: break out to a new tab. A soft-navigate would replace
              // the host page's ~560px frame with the reader and strand them.
              {...(embedded ? { target: "_blank", rel: "noopener noreferrer" } : {})}
              onClick={(event) => {
                if (embedded) return;
                event.preventDefault();
                softNavigate(readerUrl);
              }}
            >
              {strings.citationOpenInReader}
            </a>
          ) : null}
          {typeof citation.book === "string" && typeof citation.chapter === "number" ? (
            // Task 224: into the connections graph focused on this chapter.
            <a
              className="source__reader"
              href={connectionsPath(`c:${citation.book}-${citation.chapter}`)}
              {...(embedded ? { target: "_blank", rel: "noopener noreferrer" } : {})}
              onClick={(event) => {
                if (embedded) return;
                event.preventDefault();
                softNavigate(connectionsPath(`c:${citation.book}-${citation.chapter}`));
              }}
            >
              {strings.citationViewConnections}
            </a>
          ) : null}
          {href !== null ? (
            <a
              className="source__link"
              href={href}
              target="_blank"
              rel="noopener noreferrer"
            >
              {strings.citationOpenLabel}
            </a>
          ) : null}
        </div>
      </div>
    </article>
  );
}
